
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import Button from '../shared/Button';
import { UtensilsCrossed, Lock, Mail, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { send } from '@emailjs/browser';

const Login: React.FC = () => {
  const { login, updateUser, users } = useData();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const user = users.find(u => u.email === email && u.password === password);
    
    if (user) {
      if (user.isPasswordReset) {
        setIsResetMode(true);
        return;
      }
      await login(email, password);
    } else {
      setError('Credenciais inválidas. Verifique seu login e senha.');
    }
  };

  const handlePasswordReset = (e: React.FormEvent) => {
    e.preventDefault();
    const user = users.find(u => u.email === email);
    if (user && newPassword) {
      updateUser({ ...user, password: newPassword, isPasswordReset: false });
      login(email, newPassword);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Por favor, digite um login ou e-mail no campo acima antes de clicar em recuperar.');
      const emailInput = document.querySelector('input[type="text"]') as HTMLInputElement;
      if (emailInput) emailInput.focus();
      return;
    }

    const user = users.find(u => u.email.toLowerCase() === trimmedEmail.toLowerCase());
    
    if (user) {
      // Prioridade: Email de recuperação para Admin, caso contrário usa o email de login se for um email válido
      const targetEmail = user.role === 'admin' && user.recoveryEmail ? user.recoveryEmail : user.email;

      if (!targetEmail || !targetEmail.includes('@')) {
        setError('Este usuário não possui um endereço de e-mail de recuperação válido configurado.');
        return;
      }

      setIsLoading(true);
      
      try {
        const templateParams = {
          to_email: targetEmail,
          email: targetEmail,
          to_name: user.role === 'admin' ? 'Administrador' : user.email.split('@')[0],
          user_name: user.email,
          recovery_password: user.password
        };

        // --- CHAVES CONFIGURADAS ---
        const SERVICE_ID = 'service_v3d9gda'; 
        const TEMPLATE_ID = 'template_7f3do2i';
        const PUBLIC_KEY = 'DriZ_Ii4q4o8Qlv4T';

        await send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

        setSuccess(`E-mail de recuperação enviado com sucesso para ${targetEmail}!`);
      } catch (err: any) {
        console.error('Erro EmailJS Detalhado:', JSON.stringify(err));
        
        let errorMessage = 'O servidor de e-mail não pôde processar a solicitação agora.';
        if (err?.status === 422) {
          errorMessage = 'Erro de configuração no serviço de e-mail.';
        }

        setError(errorMessage);
        alert(`[RECUPERAÇÃO DE SENHA]\nSua senha é: ${user.password}\n\n(O e-mail não foi enviado devido a um erro técnico: ${err?.status || 'desconhecido'})`);
      } finally {
        setIsLoading(false);
      }
    } else {
      setError('Usuário não encontrado.');
    }
  };

  if (isResetMode) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 border border-slate-200">
          <div className="text-center mb-8">
            <div className="bg-amber-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto text-amber-600 mb-4">
              <AlertCircle size={32} />
            </div>
            <h2 className="text-2xl font-black text-slate-800">Definir Nova Senha</h2>
            <p className="text-slate-500 text-sm mt-2">Sua senha foi resetada. Escolha uma nova senha de acesso.</p>
          </div>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Nova Senha</label>
              <input 
                type="password" 
                required 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 outline-none focus:ring-2 focus:ring-indigo-500 transition-all" 
                placeholder="Digite sua nova senha" 
              />
            </div>
            <Button type="submit" className="w-full py-4 text-base shadow-lg shadow-indigo-100">Salvar e Entrar</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <div className="bg-indigo-600 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto text-white shadow-2xl shadow-indigo-200 mb-6 transform rotate-12">
            <UtensilsCrossed size={40} />
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">4Nutri</h1>
          <p className="text-slate-500 font-medium mt-2">Gestão Inteligente para Nutricionistas</p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-center gap-3 rounded-r-xl animate-in shake-in">
              <AlertCircle size={18} /> {error}
            </div>
          )}
          
          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm flex items-center gap-3 rounded-r-xl animate-in fade-in">
              <CheckCircle size={18} /> {success}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Login / Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                <input 
                  type="text" 
                  required 
                  value={email} 
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 pl-12 outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-800"
                  placeholder="Seu email cadastrado"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 ml-1">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest">Senha</label>
                <button 
                  type="button" 
                  onClick={handleForgotPassword} 
                  disabled={isLoading}
                  className="text-xs font-bold text-indigo-600 hover:underline disabled:opacity-50 flex items-center gap-1"
                >
                  {isLoading && <Loader2 size={12} className="animate-spin" />}
                  {isLoading ? 'Enviando...' : 'Esqueci a senha'}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                <input 
                  type="password" 
                  required 
                  value={password} 
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 pl-12 outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-800"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full py-4 text-base font-bold shadow-xl shadow-indigo-100 group"
              disabled={isLoading}
              icon={isLoading ? <Loader2 className="animate-spin" size={20} /> : null}
            >
              {isLoading ? 'Aguarde...' : 'Acessar Painel'}
            </Button>
          </form>
        </div>

        <p className="text-center text-slate-400 text-xs mt-8">
          &copy; 2024 4Nutri v2.5 • Todos os direitos reservados
        </p>
      </div>
    </div>
  );
};

export default Login;
